import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  CalendarPlus,
  FileText,
  Link2,
  Mail,
  MessageCircleMore,
  PencilLine,
  Plus,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import {FormEvent, useMemo, useState} from 'react';
import {Link} from '../router';
import {apiRequest} from '../api';
import {Modal, PrimaryButton, SecondaryButton, Surface, TextInput, Textarea} from '../design';
import {Opportunity, PageResult, Relationship, humanizeStage} from '../types';
import {LoadingPanel, formatDateTime} from './PagePrimitives';

type ConversationMessage = {
  id: string;
  version: number;
  providerMessageId: string;
  occurredAt: string;
  from: string;
  to: string;
  cc: string;
  subject: string;
  body: string;
  bodyTruncated?: boolean;
  attachmentCount?: number;
};

type Conversation = {
  id: string;
  version: number;
  provider?: 'gmail';
  relationshipId?: string;
  relationshipIds?: string[];
  opportunityIds: string[];
  occurredAt: string;
  channel: 'email' | 'phone' | 'video' | 'in_person' | 'chat' | 'other';
  summary: string;
  sourceRefs: string[];
  updatedAt: string;
  messageCount?: number;
  messages?: ConversationMessage[];
  messagesTruncated?: boolean;
};

type TimelinePayload = {
  relationship: Relationship & {opportunityIds?: string[]; phone?: string};
  conversations: Conversation[];
  opportunities: Opportunity[];
  notes: Array<{id: string; title: string; markdown: string; linkedEntityId?: string}>;
  documents: Array<{id: string; name?: string; filename?: string; sourceConversationId?: string}>;
};

type MessageDraft = Pick<ConversationMessage, 'from' | 'to' | 'cc' | 'subject' | 'body'>;
type MessageCorrection = {
  conversationId: string;
  messageId: string;
  expectedVersion: number;
  patch: Partial<MessageDraft>;
};
type MessageRemoval = Omit<MessageCorrection, 'patch'>;
type ConversationRemoval = {
  conversationId: string;
  expectedVersion: number;
};

const localNow = (): string => {
  const date = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const RelationshipDetail = ({
  relationshipId,
  opened,
  onClose,
}: {
  relationshipId: string | null;
  opened: boolean;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [conversationOpen, setConversationOpen] = useState(false);
  const [occurredAt, setOccurredAt] = useState(localNow);
  const [channel, setChannel] = useState<Conversation['channel']>('email');
  const [summary, setSummary] = useState('');
  const [opportunityIds, setOpportunityIds] = useState<string[]>([]);
  const [newCompany, setNewCompany] = useState('');
  const [newRole, setNewRole] = useState('');
  const [proposalCreated, setProposalCreated] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [messageDraft, setMessageDraft] = useState<MessageDraft | null>(null);
  const [confirmRemoveMessageId, setConfirmRemoveMessageId] = useState<string | null>(null);
  const [confirmRemoveConversationId, setConfirmRemoveConversationId] = useState<string | null>(null);

  const timeline = useQuery({
    queryKey: ['relationship-timeline', relationshipId],
    queryFn: () =>
      apiRequest<TimelinePayload>('/relationships/' + encodeURIComponent(relationshipId!) + '/timeline'),
    enabled: opened && Boolean(relationshipId),
  });

  const opportunities = useQuery({
    queryKey: ['opportunities'],
    queryFn: () => apiRequest<PageResult<Opportunity>>('/opportunities?limit=200'),
    enabled: opened,
  });

  const createConversation = useMutation({
    mutationFn: () => {
      if (!relationshipId) throw new Error('The relationship is not selected.');
      return apiRequest<Conversation>('/conversations', {
        method: 'POST',
        body: JSON.stringify({
          relationshipId,
          opportunityIds,
          occurredAt: new Date(occurredAt).toISOString(),
          channel,
          summary,
          sourceRefs: [],
        }),
      });
    },
    onSuccess: async () => {
      setSummary('');
      setOpportunityIds([]);
      setOccurredAt(localNow());
      setConversationOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['relationship-timeline', relationshipId]}),
        queryClient.invalidateQueries({queryKey: ['relationships']}),
        queryClient.invalidateQueries({queryKey: ['action-items']}),
      ]);
    },
  });

  const createLinkedOpportunity = useMutation({
    mutationFn: async () => {
      if (!relationshipId || !timeline.data) throw new Error('The relationship is not loaded.');
      const created = await apiRequest<Opportunity>('/opportunities', {
        method: 'POST',
        body: JSON.stringify({
          companyName: newCompany,
          title: newRole,
          stage: 'interested',
          source: 'manual',
          contactNames: [timeline.data.relationship.name],
          blockers: [],
          relationshipIds: [relationshipId],
        }),
      });
      const existing = timeline.data.relationship.opportunityIds ?? timeline.data.opportunities.map((item) => item.id);
      await apiRequest('/relationships/' + encodeURIComponent(relationshipId), {
        method: 'PATCH',
        body: JSON.stringify({opportunityIds: [...new Set([...existing, created.id])]}),
      });
      return created;
    },
    onSuccess: async (created) => {
      setOpportunityIds((ids) => [...new Set([...ids, created.id])]);
      setNewCompany('');
      setNewRole('');
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['relationship-timeline', relationshipId]}),
        queryClient.invalidateQueries({queryKey: ['opportunities']}),
        queryClient.invalidateQueries({queryKey: ['relationships']}),
      ]);
    },
  });

  const createProposal = useMutation({
    mutationFn: (conversation: Conversation) => {
      const sourceEmailRef = conversation.provider === 'gmail' ? conversation.sourceRefs[0] : undefined;
      return apiRequest<{id: string}>('/calendar/proposals', {
        method: 'POST',
        body: JSON.stringify({
          ...(conversation.opportunityIds[0] ? {opportunityId: conversation.opportunityIds[0]} : {}),
          sourceConversationId: conversation.id,
          ...(sourceEmailRef ? {sourceEmailRef} : {}),
        }),
      });
    },
    onSuccess: async (proposal) => {
      setProposalCreated(proposal.id);
      await Promise.all([
        queryClient.invalidateQueries({queryKey: ['action-items']}),
        queryClient.invalidateQueries({queryKey: ['calendar-proposals']}),
      ]);
    },
  });

  const refreshImportedSources = async () => {
    await Promise.all([
      queryClient.invalidateQueries({queryKey: ['relationship-timeline', relationshipId]}),
      queryClient.invalidateQueries({queryKey: ['relationships']}),
      queryClient.invalidateQueries({queryKey: ['action-items']}),
      queryClient.invalidateQueries({queryKey: ['calendar-proposals']}),
    ]);
  };

  const correctImportedMessage = useMutation({
    mutationFn: ({conversationId, messageId, expectedVersion, patch}: MessageCorrection) =>
      apiRequest('/conversations/' + encodeURIComponent(conversationId) + '/messages/' + encodeURIComponent(messageId), {
        method: 'PATCH',
        body: JSON.stringify({expectedVersion, ...patch}),
      }),
    onSuccess: async () => {
      setEditingMessageId(null);
      setMessageDraft(null);
      await refreshImportedSources();
    },
    onError: async () => {
      await queryClient.invalidateQueries({queryKey: ['relationship-timeline', relationshipId]});
    },
  });

  const removeImportedMessage = useMutation({
    mutationFn: ({conversationId, messageId, expectedVersion}: MessageRemoval) =>
      apiRequest('/conversations/' + encodeURIComponent(conversationId) + '/messages/' + encodeURIComponent(messageId), {
        method: 'DELETE',
        body: JSON.stringify({expectedVersion}),
      }),
    onSuccess: async () => {
      setConfirmRemoveMessageId(null);
      setEditingMessageId(null);
      setMessageDraft(null);
      await refreshImportedSources();
    },
    onError: async () => {
      await queryClient.invalidateQueries({queryKey: ['relationship-timeline', relationshipId]});
    },
  });

  const removeImportedConversation = useMutation({
    mutationFn: ({conversationId, expectedVersion}: ConversationRemoval) =>
      apiRequest('/conversations/' + encodeURIComponent(conversationId) + '/imported-data', {
        method: 'DELETE',
        body: JSON.stringify({expectedVersion}),
      }),
    onSuccess: async () => {
      setConfirmRemoveConversationId(null);
      setConfirmRemoveMessageId(null);
      setEditingMessageId(null);
      setMessageDraft(null);
      await refreshImportedSources();
    },
    onError: async () => {
      await queryClient.invalidateQueries({queryKey: ['relationship-timeline', relationshipId]});
    },
  });

  const beginMessageCorrection = (message: ConversationMessage) => {
    correctImportedMessage.reset();
    removeImportedMessage.reset();
    setConfirmRemoveMessageId(null);
    setConfirmRemoveConversationId(null);
    setEditingMessageId(message.id);
    setMessageDraft({
      from: message.from,
      to: message.to,
      cc: message.cc,
      subject: message.subject,
      body: message.body,
    });
  };

  const updateMessageDraft = (field: keyof MessageDraft, value: string) => {
    setMessageDraft((draft) => draft ? {...draft, [field]: value} : draft);
  };

  const messageCorrectionPatch = (message: ConversationMessage): Partial<MessageDraft> => {
    if (!messageDraft) return {};
    return Object.fromEntries(
      (Object.keys(messageDraft) as Array<keyof MessageDraft>)
        .filter((field) => messageDraft[field] !== message[field])
        .map((field) => [field, messageDraft[field]]),
    ) as Partial<MessageDraft>;
  };

  const opportunityMap = useMemo(
    () => new Map((opportunities.data?.items ?? timeline.data?.opportunities ?? []).map((item) => [item.id, item])),
    [opportunities.data, timeline.data],
  );

  return (
    <Modal opened={opened} onClose={onClose} title="Relationship history" centered size="xl">
      {timeline.isLoading ? <LoadingPanel label="Loading relationship timeline" /> : null}
      {timeline.isError ? (
        <p className="pd-form-error" role="alert">
          {timeline.error instanceof Error ? timeline.error.message : 'The relationship could not be loaded.'}
        </p>
      ) : null}
      {timeline.data ? (
        <div className="pd-relationship-detail">
          <header>
            <div className="pd-avatar" aria-hidden="true">
              {timeline.data.relationship.name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <span className="pd-eyebrow">{humanizeStage(timeline.data.relationship.stage)}</span>
              <h2>{timeline.data.relationship.name}</h2>
              <p>
                {[timeline.data.relationship.agency, ...timeline.data.relationship.emails].filter(Boolean).join(' · ')}
              </p>
            </div>
            <PrimaryButton
              leftSection={<MessageCircleMore size={16} aria-hidden="true" />}
              onClick={() => setConversationOpen((value) => !value)}
            >
              Add conversation
            </PrimaryButton>
          </header>

          {conversationOpen ? (
            <Surface>
              <form
                className="pd-conversation-form"
                onSubmit={(event: FormEvent) => {
                  event.preventDefault();
                  createConversation.mutate();
                }}
              >
                <div className="pd-note-heading">
                  <div><span className="pd-eyebrow">Manual interaction</span><h3>Record what happened.</h3></div>
                  <span>PursuitDeck never sends this message.</span>
                </div>
                <div className="pd-conversation-fields">
                  <label>
                    <span>When</span>
                    <input
                      type="datetime-local"
                      required
                      value={occurredAt}
                      onChange={(event) => setOccurredAt(event.currentTarget.value)}
                    />
                  </label>
                  <label>
                    <span>Channel</span>
                    <select value={channel} onChange={(event) => setChannel(event.currentTarget.value as Conversation['channel'])}>
                      {(['email', 'phone', 'video', 'in_person', 'chat', 'other'] as const).map((value) => (
                        <option key={value} value={value}>{humanizeStage(value)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="pd-calendar-field-wide">
                    <span>Summary</span>
                    <textarea
                      required
                      rows={5}
                      value={summary}
                      onChange={(event) => setSummary(event.currentTarget.value)}
                      placeholder="Topics, decisions, promises, and the next step"
                    />
                  </label>
                </div>

                <fieldset className="pd-opportunity-links">
                  <legend>Link one or more opportunities</legend>
                  {(opportunities.data?.items ?? []).map((item) => (
                    <label key={item.id}>
                      <input
                        type="checkbox"
                        checked={opportunityIds.includes(item.id)}
                        onChange={(event) =>
                          setOpportunityIds((ids) =>
                            event.currentTarget.checked
                              ? [...ids, item.id]
                              : ids.filter((id) => id !== item.id),
                          )
                        }
                      />
                      <span>{item.title} · {item.companyName}</span>
                    </label>
                  ))}
                </fieldset>

                <div className="pd-linked-opportunity-create">
                  <TextInput
                    label="New company"
                    value={newCompany}
                    onChange={(event) => setNewCompany(event.currentTarget.value)}
                  />
                  <TextInput
                    label="New role"
                    value={newRole}
                    onChange={(event) => setNewRole(event.currentTarget.value)}
                  />
                  <SecondaryButton
                    type="button"
                    leftSection={<Plus size={15} aria-hidden="true" />}
                    disabled={!newCompany.trim() || !newRole.trim()}
                    loading={createLinkedOpportunity.isPending}
                    onClick={() => createLinkedOpportunity.mutate()}
                  >
                    Create and link
                  </SecondaryButton>
                </div>

                {createConversation.isError || createLinkedOpportunity.isError ? (
                  <p className="pd-form-error" role="alert">
                    {(createConversation.error ?? createLinkedOpportunity.error) instanceof Error
                      ? (createConversation.error ?? createLinkedOpportunity.error as Error).message
                      : 'The relationship update could not be saved.'}
                  </p>
                ) : null}
                <div className="pd-form-actions">
                  <SecondaryButton type="button" onClick={() => setConversationOpen(false)}>Cancel</SecondaryButton>
                  <PrimaryButton type="submit" loading={createConversation.isPending} disabled={!summary.trim()}>
                    Save conversation
                  </PrimaryButton>
                </div>
              </form>
            </Surface>
          ) : null}

          <section className="pd-linked-opportunities" aria-labelledby="linked-opportunities-title">
            <div>
              <span className="pd-eyebrow">Durable links</span>
              <h3 id="linked-opportunities-title">{timeline.data.opportunities.length} represented opportunit{timeline.data.opportunities.length === 1 ? 'y' : 'ies'}</h3>
            </div>
            {timeline.data.opportunities.map((item) => (
              <Link key={item.id} to={'/opportunities/' + encodeURIComponent(item.id)}>
                <Link2 size={15} aria-hidden="true" />
                <span>{item.title}<small>{item.companyName}</small></span>
              </Link>
            ))}
          </section>

          <ol className="pd-conversation-timeline" aria-label="Conversation timeline">
            {timeline.data.conversations.map((conversation) => {
              const linked = conversation.opportunityIds.map((id) => opportunityMap.get(id)).filter(Boolean) as Opportunity[];
              const attachments = timeline.data.documents.filter((item) => item.sourceConversationId === conversation.id);
              return (
                <li key={conversation.id}>
                  <span aria-hidden="true" />
                  <Surface>
                    <div className="pd-card-topline">
                      <strong>{humanizeStage(conversation.channel)}</strong>
                      <time dateTime={conversation.occurredAt}>{formatDateTime(conversation.occurredAt)}</time>
                    </div>
                    <p>{conversation.summary}</p>
                    {linked.length ? (
                      <div className="pd-timeline-links">
                        {linked.map((item) => <Link key={item.id} to={'/opportunities/' + item.id}>{item.title} · {item.companyName}</Link>)}
                      </div>
                    ) : null}
                    {conversation.provider === 'gmail' ? (
                      <details className="pd-email-thread">
                        <summary>
                          Recent Gmail messages · {conversation.messages?.length ?? 0} of {conversation.messageCount ?? conversation.messages?.length ?? 0}
                        </summary>
                        {conversation.messages?.length ? (
                          <ol>
                          {conversation.messages.map((message) => {
                            const compactBody = message.body.replace(/\s+/g, ' ').trim();
                            const preview = compactBody.slice(0, 640);
                            const correctionPatch = editingMessageId === message.id
                              ? messageCorrectionPatch(message)
                              : {};
                            return (
                              <li key={message.id}>
                                <div>
                                  <strong>{message.subject || 'Email message'}</strong>
                                  <time dateTime={message.occurredAt}>{formatDateTime(message.occurredAt)}</time>
                                </div>
                                <small>{message.from || 'Unknown sender'}</small>
                                {editingMessageId === message.id && messageDraft ? (
                                  <form
                                    className="pd-email-correction"
                                    onSubmit={(event) => {
                                      event.preventDefault();
                                      correctImportedMessage.mutate({
                                        conversationId: conversation.id,
                                        messageId: message.id,
                                        expectedVersion: message.version,
                                        patch: correctionPatch,
                                      });
                                    }}
                                  >
                                    <div className="pd-email-correction-grid">
                                      <TextInput
                                        label="From"
                                        maxLength={4 * 1_024}
                                        value={messageDraft.from}
                                        onChange={(event) => updateMessageDraft('from', event.currentTarget.value)}
                                      />
                                      <TextInput
                                        label="To"
                                        maxLength={4 * 1_024}
                                        value={messageDraft.to}
                                        onChange={(event) => updateMessageDraft('to', event.currentTarget.value)}
                                      />
                                      <TextInput
                                        label="Cc"
                                        maxLength={4 * 1_024}
                                        value={messageDraft.cc}
                                        onChange={(event) => updateMessageDraft('cc', event.currentTarget.value)}
                                      />
                                      <TextInput
                                        label="Subject"
                                        maxLength={4 * 1_024}
                                        value={messageDraft.subject}
                                        onChange={(event) => updateMessageDraft('subject', event.currentTarget.value)}
                                      />
                                    </div>
                                    <Textarea
                                      label="Imported message text"
                                      description="This corrects PursuitDeck's private copy. It does not change the email in Gmail."
                                      autosize
                                      minRows={4}
                                      maxRows={12}
                                      maxLength={64 * 1_024}
                                      value={messageDraft.body}
                                      onChange={(event) => updateMessageDraft('body', event.currentTarget.value)}
                                    />
                                    {correctImportedMessage.isError
                                      && correctImportedMessage.variables?.messageId === message.id ? (
                                        <p className="pd-form-error" role="alert">
                                          {correctImportedMessage.error instanceof Error
                                            ? correctImportedMessage.error.message
                                            : 'The imported message could not be corrected.'}
                                        </p>
                                      ) : null}
                                    <div className="pd-email-message-actions">
                                      <SecondaryButton
                                        type="button"
                                        size="xs"
                                        onClick={() => {
                                          setEditingMessageId(null);
                                          setMessageDraft(null);
                                        }}
                                      >
                                        Cancel
                                      </SecondaryButton>
                                      <PrimaryButton
                                        type="submit"
                                        size="xs"
                                        loading={correctImportedMessage.isPending
                                          && correctImportedMessage.variables?.messageId === message.id}
                                        disabled={!Object.keys(correctionPatch).length}
                                      >
                                        Save corrected copy
                                      </PrimaryButton>
                                    </div>
                                  </form>
                                ) : (
                                  <>
                                    <p>
                                      {preview || 'No text content.'}
                                      {compactBody.length > preview.length || message.bodyTruncated ? '…' : ''}
                                    </p>
                                    <div className="pd-email-message-actions">
                                      <SecondaryButton
                                        type="button"
                                        size="xs"
                                        leftSection={<PencilLine size={14} aria-hidden="true" />}
                                        onClick={() => beginMessageCorrection(message)}
                                      >
                                        Correct imported copy
                                      </SecondaryButton>
                                      {confirmRemoveMessageId === message.id ? (
                                        <>
                                          <span role="status">
                                            Remove this imported message and its stored attachments? Gmail stays unchanged.
                                          </span>
                                          <SecondaryButton
                                            type="button"
                                            size="xs"
                                            onClick={() => setConfirmRemoveMessageId(null)}
                                          >
                                            Keep copy
                                          </SecondaryButton>
                                          <SecondaryButton
                                            type="button"
                                            size="xs"
                                            color="red"
                                            loading={removeImportedMessage.isPending
                                              && removeImportedMessage.variables?.messageId === message.id}
                                            onClick={() => removeImportedMessage.mutate({
                                              conversationId: conversation.id,
                                              messageId: message.id,
                                              expectedVersion: message.version,
                                            })}
                                          >
                                            Confirm removal
                                          </SecondaryButton>
                                        </>
                                      ) : (
                                        <SecondaryButton
                                          type="button"
                                          size="xs"
                                          color="red"
                                          leftSection={<Trash2 size={14} aria-hidden="true" />}
                                          onClick={() => {
                                            removeImportedMessage.reset();
                                            setConfirmRemoveConversationId(null);
                                            setEditingMessageId(null);
                                            setMessageDraft(null);
                                            setConfirmRemoveMessageId(message.id);
                                          }}
                                        >
                                          Remove imported copy
                                        </SecondaryButton>
                                      )}
                                    </div>
                                    {removeImportedMessage.isError
                                      && removeImportedMessage.variables?.messageId === message.id ? (
                                        <p className="pd-form-error" role="alert">
                                          {removeImportedMessage.error instanceof Error
                                            ? removeImportedMessage.error.message
                                            : 'The imported message could not be removed.'}
                                        </p>
                                      ) : null}
                                  </>
                                )}
                                {message.attachmentCount ? (
                                  <small>{message.attachmentCount} attachment{message.attachmentCount === 1 ? '' : 's'} imported</small>
                                ) : null}
                              </li>
                            );
                          })}
                          </ol>
                        ) : (
                          <p className="pd-email-empty">No imported messages remain in this thread.</p>
                        )}
                        {conversation.messagesTruncated ? <small>Showing the 12 most recent imported messages.</small> : null}
                        <div className="pd-email-thread-removal">
                          <p>
                            You can remove this entire imported thread, its stored attachments, and source-derived tasks.
                            The original Gmail thread is never changed.
                          </p>
                          {confirmRemoveConversationId === conversation.id ? (
                            <div className="pd-email-message-actions" role="group" aria-label="Confirm imported thread removal">
                              <span role="status">This cannot be undone inside PursuitDeck.</span>
                              <SecondaryButton
                                type="button"
                                size="xs"
                                onClick={() => setConfirmRemoveConversationId(null)}
                              >
                                Keep thread
                              </SecondaryButton>
                              <SecondaryButton
                                type="button"
                                size="xs"
                                color="red"
                                loading={removeImportedConversation.isPending
                                  && removeImportedConversation.variables?.conversationId === conversation.id}
                                onClick={() => removeImportedConversation.mutate({
                                  conversationId: conversation.id,
                                  expectedVersion: conversation.version,
                                })}
                              >
                                Remove entire imported thread
                              </SecondaryButton>
                            </div>
                          ) : (
                            <SecondaryButton
                              type="button"
                              size="xs"
                              color="red"
                              leftSection={<Trash2 size={14} aria-hidden="true" />}
                              onClick={() => {
                                removeImportedConversation.reset();
                                setConfirmRemoveMessageId(null);
                                setEditingMessageId(null);
                                setMessageDraft(null);
                                setConfirmRemoveConversationId(conversation.id);
                              }}
                            >
                              Remove imported thread
                            </SecondaryButton>
                          )}
                          {removeImportedConversation.isError
                            && removeImportedConversation.variables?.conversationId === conversation.id ? (
                              <p className="pd-form-error" role="alert">
                                {removeImportedConversation.error instanceof Error
                                  ? removeImportedConversation.error.message
                                  : 'The imported Gmail thread could not be removed.'}
                              </p>
                            ) : null}
                        </div>
                      </details>
                    ) : null}
                    {conversation.sourceRefs.length ? (
                      <small className="pd-source-line">
                        <Mail size={13} aria-hidden="true" />
                        Gmail source · {conversation.messageCount ?? conversation.sourceRefs.length} imported · latest {conversation.sourceRefs[0]}
                      </small>
                    ) : null}
                    {attachments.length ? (
                      <small className="pd-source-line">
                        <FileText size={13} aria-hidden="true" />
                        {attachments.map((item) => item.name ?? item.filename ?? 'Attachment').join(', ')}
                      </small>
                    ) : null}
                    <SecondaryButton
                      leftSection={<CalendarPlus size={15} aria-hidden="true" />}
                      loading={createProposal.isPending && createProposal.variables?.id === conversation.id}
                      onClick={() => createProposal.mutate(conversation)}
                    >
                      Propose private event
                    </SecondaryButton>
                  </Surface>
                </li>
              );
            })}
          </ol>

          {!timeline.data.conversations.length ? (
            <div className="pd-empty-copy">No conversations yet. Add a call, meeting, chat, or other interaction above.</div>
          ) : null}
          {proposalCreated ? (
            <div className="pd-inline-status" role="status">
              <ShieldCheck size={17} aria-hidden="true" />
              Private calendar proposal created. Review it in Action Inbox before anything reaches Calendar.
            </div>
          ) : null}
          {createProposal.isError ? (
            <p className="pd-form-error" role="alert">
              {createProposal.error instanceof Error ? createProposal.error.message : 'The calendar proposal could not be created.'}
            </p>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
};
